// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { DnaSyngoviaConnection } from '../../interfaces/dna-syngovia-connection';
import { DnaSyngoviaConnectionRect } from '../../interfaces/dna-syngovia-connection-rect';
import { DnaSyngoviaDataConnections } from '../../interfaces/dna-syngovia-data-connections';
import { DnaSyngoviaGroupNodeEType } from '../../interfaces/dna-syngovia-group-node';
import { DnaSyngoviaNodeEType } from '../../interfaces/dna-syngovia-node';
import { AbstractChartMapService } from './abstract-chart-map.service';
import { ClientChartMapService } from './client-chart-map.service';
import { DICOMProxyChartMapService } from './dicom-proxy-chart-map.service';
import { DigitalProductChartMapSevice } from './digital-product-chart-map.service';
import { ModalityChartMapService } from './modality-chart-map.service';
import { ServerChartMapService } from './server-chart-map.service';

export type ConnectionsRectByType = {
  [key: string]: DnaSyngoviaConnectionRect[];
};

@Injectable({
  providedIn: 'root',
})
export class ConnectionsRectService {
  public constructor(
    private readonly clientChartMapService: ClientChartMapService,
    private readonly serverChartMapService: ServerChartMapService,
    private readonly modalityChartMapService: ModalityChartMapService,
    private readonly dicomProxyChartMapService: DICOMProxyChartMapService,
    private readonly digitalProductChartMapService: DigitalProductChartMapSevice,
  ) {}

  public newConnectionsRectByType(): ConnectionsRectByType {
    return {
      server: [],
      modality: [],
      digital_product: [],
      client: [],
      dicom_proxy: [],
    };
  }

  public serviceByType(
    groupNodeType: string,
  ): AbstractChartMapService | undefined {
    switch (groupNodeType) {
      case DnaSyngoviaGroupNodeEType.SERVER:
        return this.serverChartMapService as AbstractChartMapService;

      case DnaSyngoviaGroupNodeEType.MODALITY:
        return this.modalityChartMapService as AbstractChartMapService;

      case DnaSyngoviaGroupNodeEType.CLIENT:
        return this.clientChartMapService as AbstractChartMapService;

      case DnaSyngoviaGroupNodeEType.DIGITAL_PRODUCT:
        return this.digitalProductChartMapService as AbstractChartMapService;

      case DnaSyngoviaGroupNodeEType.DICOM_PROXY:
        return this.dicomProxyChartMapService as AbstractChartMapService;

      default:
        return undefined;
    }
  }

  public filterConnections(
    connectionsRectByType: ConnectionsRectByType,
    dataConnections: DnaSyngoviaDataConnections,
    groupNodeType: string,
  ): void {
    let result: DnaSyngoviaConnectionRect[] = [];
    if (DnaSyngoviaGroupNodeEType.SERVER === groupNodeType) {
      const serverConnection = {
        groupNode: dataConnections.serverGroup,
        warnings: [],
      };

      result = [this.connectionsRectMapper(serverConnection)];
    } else {
      result = dataConnections.connections
        .filter(this.groupNodeTypeFilter(groupNodeType))
        .sort(this.sortConnections)
        .map(this.connectionsRectMapper);
    }

    if (DnaSyngoviaGroupNodeEType.MODALITY === groupNodeType) {
      result.sort(this.sortRectConnections);
      result.sort(this.sortRectConnectionsByWarning);

      const otherModalitiesIndex = result.findIndex(
        (rect) =>
          DnaSyngoviaNodeEType.OTHER_MODALITIES ===
          rect.connection.groupNode.nodes[0].type,
      );

      if (otherModalitiesIndex > -1) {
        const otherModalities =
          result[otherModalitiesIndex].connection.groupNode;

        if (
          otherModalities &&
          (!otherModalities.warnings || otherModalities.warnings?.length === 0)
        ) {
          const numOfColumns = this.clientChartMapService.NUM_OF_COLUMNS;
          const numOfRows = Math.ceil(result.length / numOfColumns);

          const otherModalitiesRect = result.splice(otherModalitiesIndex, 1);

          result.splice(
            (numOfRows - 1) * numOfColumns,
            0,
            otherModalitiesRect[0],
          );
        }
      }
    }

    connectionsRectByType[groupNodeType] = result;
  }

  private readonly sortRectConnectionsByWarning = (
    rectA: DnaSyngoviaConnectionRect,
    rectB: DnaSyngoviaConnectionRect,
  ): number => {
    const warningA = rectA.connection.groupNode.warnings;
    const warningB = rectB.connection.groupNode.warnings;

    return (warningB?.length ?? 0) - (warningA?.length ?? 0);
  };

  private readonly sortRectConnections = (
    rectA: DnaSyngoviaConnectionRect,
    rectB: DnaSyngoviaConnectionRect,
  ): number => {
    const descA = rectA.connection.groupNode.description;
    const descB = rectB.connection.groupNode.description;
    if (descA === descB) {
      return 0;
    } else {
      return descA > descB ? -1 : 1;
    }
  };

  private readonly sortConnections = (
    connA: DnaSyngoviaConnection,
    connB: DnaSyngoviaConnection,
  ): number => {
    const descA = connA.groupNode.description;
    const descB = connB.groupNode.description;

    if (descA === descB) {
      return 0;
    } else {
      return descA > descB ? 1 : -1;
    }
  };

  public connectionsRectMapper = (
    connection: DnaSyngoviaConnection,
  ): DnaSyngoviaConnectionRect => ({
    connection,
    rect: new DOMRect(),
    linePath: null,
    warningRect: null,
  });

  private groupNodeTypeFilter(
    groupNodeType: string,
  ): (connection: DnaSyngoviaConnection) => boolean {
    return (connection) => connection.groupNode.type === groupNodeType;
  }
}
